import React from "react";
import { PieChart, Pie } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

interface MessagesByType {
  type: string
  count: number
  percentage: number
}

interface StatsWithPieChartProps {
  data: MessagesByType[]
  loading?: boolean
}

const chartConfig = {
  conversation: {
    label: "Texto",
    color: "#3d3d42",
  },
  imageMessage: {
    label: "Imagem",
    color: "#505055",
  },
  videoMessage: {
    label: "Vídeo",
    color: "#29292d",
  },
  audioMessage: {
    label: "Áudio",
    color: "#6b6b70",
  },
  documentMessage: {
    label: "Documento",
    color: "#29292d",
  },
  other: {
    label: "Outros",
    color: "#1a1a1d",
  },
} satisfies ChartConfig;

const StatsWithPieChart: React.FC<StatsWithPieChartProps> = ({ data, loading = false }) => {
  const formatTypeLabel = (type: string): string => {
    const typeMap: Record<string, string> = {
      'conversation': 'Texto',
      'extendedTextMessage': 'Texto',
      'imageMessage': 'Imagem',
      'videoMessage': 'Vídeo',
      'audioMessage': 'Áudio',
      'documentMessage': 'Documento',
      'stickerMessage': 'Sticker',
      'locationMessage': 'Localização',
      'contactMessage': 'Contato',
      'reactionMessage': 'Reação',
      'protocolMessage': 'Sistema'
    }
    return typeMap[type] || type
  }

  const chartData = data.map(item => ({
    type: formatTypeLabel(item.type),
    count: item.count,
    fill: `var(--color-${item.type})` || "var(--color-other)"
  }))

  if (loading) {
    return (
      <div className="w-full flex items-center flex-col justify-between gap-4 animate-pulse">
        <div className="h-6 bg-neutral-600 rounded w-1/2"></div>
        <div className="w-[280px] h-[280px] bg-neutral-600 rounded-full"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="w-full flex items-center flex-col justify-between gap-4">
        <h2 className="text-neutral-100 font-semibold text-[20px]">
          Tipos de Mensagem
        </h2>
        <div className="flex items-center justify-center h-[280px]">
          <p className="text-neutral-400 text-sm">Nenhuma mensagem encontrada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex items-center flex-col justify-between gap-4">
      <h2 className="text-neutral-100 font-semibold text-[20px]">
        Tipos de Mensagem
      </h2>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square min-h-[280px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
          <Pie data={chartData} dataKey="count" nameKey="type" />
        </PieChart>
      </ChartContainer>
    </div>
  );
};

export default StatsWithPieChart;
